import React from 'react';
import { motion } from 'framer-motion';
import { Users, Layers, Eye, Clock, Palette } from 'lucide-react';

const reasons = [
  {
    icon: Users,
    title: 'Expert Interior Designers & Planners',
    description: 'Our team of certified professionals brings decades of combined experience.',
  },
  {
    icon: Layers,
    title: 'End-to-End Design Solutions',
    description: 'From concept to completion, we handle every aspect of your project.',
  },
  {
    icon: Eye,
    title: 'Transparent Process',
    description: 'Clear communication, detailed proposals, and no hidden costs.',
  },
  {
    icon: Clock,
    title: 'On-Time Delivery',
    description: 'We respect your timeline and ensure timely project completion.',
  },
  {
    icon: Palette,
    title: 'Customized Designs for Every Budget',
    description: 'Tailored solutions that maximize value at every price point.',
  },
];

export default function WhyChoose() {
  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative z-10">
              <img
                src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&q=80"
                alt="Interior Design Process"
                className="w-full h-[600px] object-cover"
              />
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-[#C9A962] z-0" />
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FDFBF7] z-0" />
            
            {/* Experience Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="absolute -bottom-8 left-8 bg-[#C9A962] p-6 z-20"
            >
              <div className="text-white text-center">
                <div className="font-serif text-4xl">7+</div>
                <div className="text-sm tracking-wider">Years of Excellence</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-[1px] bg-[#C9A962]" />
                <span className="text-[#C9A962] tracking-[0.3em] text-xs uppercase">Why Choose Us</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-6">
                Why Choose Veda Creation & Group
              </h2>
              <p className="text-[#2C2C2C]/60 text-lg mb-10">
                We bring your vision to life with precision, creativity, and unwavering commitment to excellence.
              </p>
            </motion.div>

            <div className="space-y-6">
              {reasons.map((reason, index) => (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-5 group"
                >
                  <div className="w-14 h-14 flex-shrink-0 bg-[#FDFBF7] flex items-center justify-center group-hover:bg-[#C9A962] transition-colors duration-300">
                    <reason.icon className="w-6 h-6 text-[#C9A962] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#2C2C2C] mb-1">
                      {reason.title}
                    </h3>
                    <p className="text-[#2C2C2C]/60 text-sm">
                      {reason.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}